import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Translation resources
const resources = {
  en: {
    translation: {
      // Navigation
      nav: {
        home: 'Home',
        about: 'ABOUT US',
        streams: 'Streams',
        services: 'Services',
        team: 'Our Team',
        login: 'Login',
        register: 'Register',
        profile: 'Profile',
        dashboard: 'Dashboard',
        admin: 'Admin',
        logout: 'Logout',
        userMenu: 'User Menu',
        myEvents: 'Events',
        earnings: 'Earnings',
        chats: 'Chats'
      },
      // Home page
      home: {
        welcome: 'GFM STUDIO',
        subtitle: 'Your Partner in Digital World',
        browseStreams: 'Browse Streams',
        joinNow: 'Join Now',
        features: 'Features',
        liveBroadcasting: 'Live Broadcasting',
        liveBroadcastingDesc: 'Stream your content in real-time to audiences worldwide',
        interactiveChat: 'Interactive Chat',
        interactiveChatDesc: 'Engage with your audience through real-time chat',
        analyticsDashboard: 'Analytics Dashboard',
        analyticsDashboardDesc: 'Track viewer metrics and stream performance',
        secureReliable: 'Secure & Reliable',
        secureReliableDesc: 'Enterprise-grade security and 99.9% uptime'
      },
      // Auth
      auth: {
        loginTitle: 'Login to GFM Studio',
        registerTitle: 'Register for GFM Studio',
        username: 'Username',
        email: 'Email',
        password: 'Password',
        confirmPassword: 'Confirm Password',
        firstName: 'First Name',
        lastName: 'Last Name',
        phone: 'Phone',
        loginButton: 'Login',
        registerButton: 'Register',
        loggingIn: 'Logging in...',
        registering: 'Registering...',
        noAccount: "Don't have an account?",
        haveAccount: 'Already have an account?',
        loginHere: 'Login here',
        registerHere: 'Register here',
        companyAccessOnly: 'This system is for company members only. Contact your administrator for access.',
        forgotPasswordLink: 'Forgot password?',
        forgotPasswordTitle: 'Reset your password',
        sendResetLink: 'Send Reset Link',
        sendingResetLink: 'Sending...',
        resetLinkSentGeneric: 'If an account with that email exists, a password reset link has been sent.',
        resetPasswordTitle: 'Set a new password',
        newPassword: 'New Password',
        confirmNewPassword: 'Confirm New Password',
        resetPasswordButton: 'Reset Password',
        resettingPassword: 'Resetting...',
        resetSuccess: 'Your password has been reset. Redirecting to login...',
        passwordsDoNotMatch: 'Passwords do not match',
        backToLogin: 'Back to login'
      },
      // Profile
      profile: {
        title: 'Profile',
        memberSince: 'Member Since',
        notProvided: 'Not provided',
        bio: 'Bio'
      },
      // Dashboard
      dashboard: {
        title: 'Coworker Dashboard',
        welcome: 'Welcome',
        analytics: 'Analytics',
        analyticsDesc: 'View your streaming statistics and viewer insights',
        totalStreams: 'Total Streams',
        myStreams: 'My Streams',
        myStreamsDesc: 'Manage your past and upcoming streams',
        createStream: 'Create Stream',
        announcements: 'Announcements',
        announcementsDesc: 'Internal announcements and updates',
        newAnnouncements: 'new announcements',
        team: 'Team',
        teamDesc: 'View and collaborate with coworkers',
        activeCoworkers: 'Active coworkers',
        dashboardIntro: 'This is your coworker dashboard. Here you can manage your streams, view analytics, and collaborate with your team.'
      },
      // Admin
      admin: {
        title: 'Admin Panel',
        systemOverview: 'System Overview',
        systemOverviewDesc: 'Complete administrative control over the GFM Studio platform.',
        userManagement: 'User Management',
        userManagementDesc: 'Manage user accounts and permissions',
        totalUsers: 'Total Users',
        streamManagement: 'Stream Management',
        streamManagementDesc: 'Monitor and manage all streams',
        activeStreams: 'Active Streams',
        contentManagement: 'Content Management',
        contentManagementDesc: 'Manage pages and announcements',
        publishedPages: 'Published Pages',
        systemSettings: 'System Settings',
        systemSettingsDesc: 'Configure platform settings',
        settings: 'Settings',
        viewAllUsers: 'View All Users',
        viewAllStreams: 'View All Streams',
        createAnnouncement: 'Create Announcement',
        systemLogs: 'System Logs'
      },
      // Streams
      streams: {
        title: 'Live Streams',
        noStreams: 'No streams available at the moment.',
        loading: 'Loading streams...',
        viewers: 'viewers',
        live: 'LIVE',
        scheduled: 'Scheduled',
        ended: 'Ended',
        cancelled: 'Cancelled',
        by: 'by',
        status: 'Status',
        scheduledStart: 'Scheduled',
        started: 'Started',
        chat: 'Chat',
        chatPlaceholder: 'Real-time chat would appear here',
        streamPlayer: 'Stream Player Would Go Here',
        streamIs: 'Stream is'
      },
      // Common
      common: {
        loading: 'Loading...',
        error: 'Error',
        success: 'Success',
        save: 'Save',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        create: 'Create',
        update: 'Update',
        search: 'Search',
        filter: 'Filter',
        sort: 'Sort',
        actions: 'Actions',
        yes: 'Yes',
        no: 'No',
        allRightsReserved: 'All rights reserved'
      },
      // Events
      events: {
        myEvents: 'My Events',
        myEventsSubtitle: 'View all events you are assigned to work on',
        noAssignedEvents: 'You have no assigned events yet.',
        errorLoading: 'Error loading events',
        errorLoadingDetail: 'Error loading event details',
        eventNotFound: 'Event not found',
        backToEvents: 'Back to Events',
        meeting: 'Meeting',
        at: 'at',
        payment: 'Payment',
        staffMembers: 'staff members',
        teamMembers: 'Team Members',
        dateTime: 'Date & Time',
        date: 'Date',
        eventTime: 'Event Time',
        startTime: 'Start Time',
        endTime: 'End Time',
        location: 'Location',
        venue: 'Venue',
        address: 'Address',
        city: 'City',
        staffMeeting: 'Staff Meeting',
        time: 'Time',
        meetingTime: 'Meeting Time',
        place: 'Place',
        clientInfo: 'Client Information',
        client: 'Client',
        contact: 'Contact',
        email: 'Email',
        myPayment: 'My Payment',
        notes: 'Internal Notes',
        eventPlan: {
          open: 'Event Plan',
          title: 'Event plan',
          template: 'Template',
          empty: 'No schedule steps are defined for this event.',
          noStartTime: 'Set an event start time to show exact clock times.',
          noTime: '——',
          eventOnly: 'Event only',
          close: 'Close',
        },
        noStaffAssigned: 'No staff assigned yet',
        you: 'You',
        types: {
          concert: 'Concert',
          sports: 'Sports Event',
          advertisement: 'Advertisement',
          corporate: 'Corporate Event',
          wedding: 'Wedding',
          conference: 'Conference',
          other: 'Other'
        },
        status: {
          on_hold: 'On Hold',
          planning: 'Planning',
          confirmed: 'Confirmed',
          done: 'Done'
        }
      },
      // Earnings
      earnings: {
        title: 'My Earnings',
        subtitle: 'Track your monthly earnings from events',
        errorLoading: 'Error loading earnings',
        totalEarnings: 'Total Earnings',
        currentMonth: 'Current Month',
        events: 'events',
        eventBreakdown: 'Event Breakdown',
        last6Months: 'Last 6 Months',
        prevMonth: 'Previous',
        nextMonth: 'Next'
      },
      team: {
        loading: 'Loading team members...',
        errorLoading: 'Error loading team members'
      }
    }
  },
  ka: {
    translation: {
      // Navigation
      nav: {
        home: 'მთავარი',
        about: 'ჩვენ შესახებ',
        streams: 'სტრიმები',
        services: 'სერვისები',
        team: 'ჩვენი გუნდი',
        login: 'შესვლა',
        register: 'რეგისტრაცია',
        profile: 'პროფილი',
        dashboard: 'დაშბორდი',
        admin: 'ადმინი',
        logout: 'გასვლა',
        userMenu: 'მომხმარებლის მენიუ',
        myEvents: 'ღონისძიებები',
        earnings: 'შემოსავალი',
        chats: 'შეტყობინებები'
      },
      // Home page
      home: {
        welcome: 'GFM STUDIO',
        subtitle: 'თქვენი პარტნიორი ციფრულ სამყაროში',
        browseStreams: 'სტრიმების დათვალიერება',
        joinNow: 'შემოგვიერთდით',
        features: 'ფუნქციები',
        liveBroadcasting: 'ლაივ მაუწყებლობა',
        liveBroadcastingDesc: 'გაუშვით თქვენი კონტენტი რეალურ დროში მსოფლიოს აუდიტორიისთვის',
        interactiveChat: 'ინტერაქტიული ჩატი',
        interactiveChatDesc: 'ურთიერთობა თქვენს აუდიტორიასთან რეალურ დროში',
        analyticsDashboard: 'ანალიტიკის დაშბორდი',
        analyticsDashboardDesc: 'თვალი ადევნეთ მაყურებლების მეტრიკას და სტრიმის შესრულებას',
        secureReliable: 'უსაფრთხო და სანდო',
        secureReliableDesc: 'საწარმოო დონის უსაფრთხოება და 99.9% uptime'
      },
      // Auth
      auth: {
        loginTitle: 'შესვლა GFM Studio-ში',
        registerTitle: 'რეგისტრაცია GFM Studio-ში',
        username: 'მომხმარებლის სახელი',
        email: 'ელ. ფოსტა',
        password: 'პაროლი',
        confirmPassword: 'დაადასტურეთ პაროლი',
        firstName: 'სახელი',
        lastName: 'გვარი',
        phone: 'ტელეფონი',
        loginButton: 'შესვლა',
        registerButton: 'რეგისტრაცია',
        loggingIn: 'შესვლა...',
        registering: 'რეგისტრაცია...',
        noAccount: 'არ გაქვთ ანგარიში?',
        haveAccount: 'უკვე გაქვთ ანგარიში?',
        loginHere: 'შედით აქ',
        registerHere: 'დარეგისტრირდით აქ',
        companyAccessOnly: 'ეს სისტემა განკუთვნილია მხოლოდ კომპანიის წევრებისთვის. წვდომისთვის დაუკავშირდით თქვენს ადმინისტრატორს.',
        forgotPasswordLink: 'დაგავიწყდათ პაროლი?',
        forgotPasswordTitle: 'პაროლის აღდგენა',
        sendResetLink: 'აღდგენის ბმულის გაგზავნა',
        sendingResetLink: 'იგზავნება...',
        resetLinkSentGeneric: 'თუ ეს ელფოსტა დარეგისტრირებულია სისტემაში, პაროლის აღდგენის ბმული გამოგზავნილია.',
        resetPasswordTitle: 'ახალი პაროლის დაყენება',
        newPassword: 'ახალი პაროლი',
        confirmNewPassword: 'გაიმეორეთ ახალი პაროლი',
        resetPasswordButton: 'პაროლის აღდგენა',
        resettingPassword: 'მიმდინარეობს...',
        resetSuccess: 'თქვენი პაროლი წარმატებით შეიცვალა. გადამისამართება შესვლის გვერდზე...',
        passwordsDoNotMatch: 'პაროლები არ ემთხვევა',
        backToLogin: 'უკან შესვლაზე'
      },
      // Profile
      profile: {
        title: 'ჩემი პროფილი',
        memberSince: 'წევრი დან',
        notProvided: 'არ არის მითითებული',
        bio: 'ჩემ შესახებ'
      },
      // Dashboard
      dashboard: {
        title: 'თანამშრომლის დაშბორდი',
        welcome: 'კეთილი იყოს თქვენი მობრძანება',
        analytics: 'ანალიტიკა',
        analyticsDesc: 'ნახეთ თქვენი სტრიმინგის სტატისტიკა და მაყურებლების ინფორმაცია',
        totalStreams: 'სულ სტრიმები',
        myStreams: 'ჩემი სტრიმები',
        myStreamsDesc: 'მართეთ თქვენი წარსული და მომავალი სტრიმები',
        createStream: 'სტრიმის შექმნა',
        announcements: 'განცხადებები',
        announcementsDesc: 'შიდა განცხადებები და ახალი ამბები',
        newAnnouncements: 'ახალი განცხადებები',
        team: 'გუნდი',
        teamDesc: 'ნახეთ და იმუშავეთ თანამშრომლებთან',
        activeCoworkers: 'აქტიური თანამშრომლები',
        dashboardIntro: 'ეს არის თქვენი თანამშრომლის დაშბორდი. აქ შეგიძლიათ მართოთ თქვენი სტრიმები, ნახოთ ანალიტიკა და იმუშაოთ თქვენს გუნდთან.'
      },
      // Admin
      admin: {
        title: 'ადმინისტრატორის პანელი',
        systemOverview: 'სისტემის მიმოხილვა',
        systemOverviewDesc: 'სრული ადმინისტრაციული კონტროლი GFM Studio პლატფორმაზე.',
        userManagement: 'მომხმარებლების მართვა',
        userManagementDesc: 'მართეთ მომხმარებლების ანგარიშები და ნებართვები',
        totalUsers: 'სულ მომხმარებლები',
        streamManagement: 'სტრიმების მართვა',
        streamManagementDesc: 'მონიტორინგი და ყველა სტრიმის მართვა',
        activeStreams: 'აქტიური სტრიმები',
        contentManagement: 'კონტენტის მართვა',
        contentManagementDesc: 'გვერდების და განცხადებების მართვა',
        publishedPages: 'გამოქვეყნებული გვერდები',
        systemSettings: 'სისტემის პარამეტრები',
        systemSettingsDesc: 'პლატფორმის პარამეტრების კონფიგურაცია',
        settings: 'პარამეტრები',
        viewAllUsers: 'ყველა მომხმარებლის ნახვა',
        viewAllStreams: 'ყველა სტრიმის ნახვა',
        createAnnouncement: 'განცხადების შექმნა',
        systemLogs: 'სისტემის ლოგები'
      },
      // Streams
      streams: {
        title: 'ლაივ სტრიმები',
        noStreams: 'ამჟამად სტრიმები არ არის ხელმისაწვდომი.',
        loading: 'სტრიმების ჩატვირთვა...',
        viewers: 'მაყურებლები',
        live: 'ლაივი',
        scheduled: 'დაგეგმილი',
        ended: 'დასრულებული',
        cancelled: 'გაუქმებული',
        by: 'მიერ',
        status: 'სტატუსი',
        scheduledStart: 'დაგეგმილი',
        started: 'დაწყებული',
        chat: 'ჩატი',
        chatPlaceholder: 'რეალურ დროში ჩატი გამოჩნდება აქ',
        streamPlayer: 'სტრიმის პლეერი გამოჩნდება აქ',
        streamIs: 'სტრიმი არის'
      },
      // Common
      common: {
        loading: 'ჩატვირთვა...',
        error: 'შეცდომა',
        success: 'წარმატება',
        save: 'შენახვა',
        cancel: 'გაუქმება',
        delete: 'წაშლა',
        edit: 'რედაქტირება',
        create: 'შექმნა',
        update: 'განახლება',
        search: 'ძებნა',
        filter: 'ფილტრი',
        sort: 'სორტირება',
        actions: 'მოქმედებები',
        yes: 'დიახ',
        no: 'არა',
        allRightsReserved: 'ყველა უფლება დაცულია'
      },
      // Events
      events: {
        myEvents: 'ჩემი ღონისძიებები',
        myEventsSubtitle: 'ნახეთ ყველა ღონისძიება, რომელზეც თქვენ ხართ დანიშნული',
        noAssignedEvents: 'თქვენ ჯერ არ გაქვთ დანიშნული ღონისძიებები.',
        errorLoading: 'შეცდომა ღონისძიებების ჩატვირთვისას',
        errorLoadingDetail: 'შეცდომა ღონისძიების დეტალების ჩატვირთვისას',
        eventNotFound: 'ღონისძიება ვერ მოიძებნა',
        backToEvents: 'უკან ღონისძიებებზე',
        meeting: 'შეხვედრა',
        at: 'საათზე',
        payment: 'ანაზღაურება',
        staffMembers: 'თანამშრომელი',
        teamMembers: 'გუნდის წევრები',
        dateTime: 'თარიღი და დრო',
        date: 'თარიღი',
        eventTime: 'ღონისძიების დრო',
        startTime: 'დაწყების დრო',
        endTime: 'დასრულების დრო',
        location: 'ლოკაცია',
        venue: 'ადგილი',
        address: 'მისამართი',
        city: 'ქალაქი',
        staffMeeting: 'პერსონალის შეხვედრა',
        time: 'დრო',
        meetingTime: 'შეხვედრის დრო',
        place: 'ადგილი',
        clientInfo: 'კლიენტის ინფორმაცია',
        client: 'კლიენტი',
        contact: 'კონტაქტი',
        email: 'ელ. ფოსტა',
        myPayment: 'ჩემი ანაზღაურება',
        notes: 'შიდა შენიშვნები',
        eventPlan: {
          open: 'ღონისძიების გეგმა',
          title: 'ღონისძიების გეგმა',
          template: 'შაბლონი',
          empty: 'ამ ღონისძიებისთვის განრიგის ნაბიჯები არ არის განსაზღვრული.',
          noStartTime: 'ზუსტი დროის საჩვენებლად დააყენეთ ღონისძიების დაწყების დრო.',
          noTime: '——',
          eventOnly: 'მხოლოდ ღონისძიება',
          close: 'დახურვა',
        },
        noStaffAssigned: 'პერსონალი ჯერ არ არის დანიშნული',
        you: 'თქვენ',
        types: {
          concert: 'კონცერტი',
          sports: 'სპორტული ღონისძიება',
          advertisement: 'რეკლამა',
          corporate: 'კორპორატიული ღონისძიება',
          wedding: 'ქორწილი',
          conference: 'კონფერენცია',
          other: 'სხვა'
        },
        status: {
          on_hold: 'მოლოდინი',
          planning: 'დაგეგმვის პროცესი',
          confirmed: 'დადასტურებული',
          done: 'დასრულებული'
        }
      },
      // Earnings
      earnings: {
        title: 'ჩემი შემოსავალი',
        subtitle: 'თვალყური ადევნეთ თვიურ შემოსავალს ღონისძიებებიდან',
        errorLoading: 'შეცდომა შემოსავლის ჩატვირთვისას',
        totalEarnings: 'სულ შემოსავალი',
        currentMonth: 'მიმდინარე თვე',
        events: 'ღონისძიება',
        eventBreakdown: 'ღონისძიებების დეტალები',
        last6Months: 'ბოლო 6 თვე',
        prevMonth: 'წინა',
        nextMonth: 'შემდეგი'
      },
      team: {
        loading: 'გუნდის წევრების ჩატვირთვა...',
        errorLoading: 'შეცდომა გუნდის წევრების ჩატვირთვისას'
      }
    }
  }
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    supportedLngs: ['en', 'ka'],
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage']
    },
    interpolation: {
      escapeValue: false
    }
  });

const syncDocumentLang = (lng) => {
  if (typeof document === 'undefined') return;
  const lang = String(lng || 'en').toLowerCase().startsWith('ka') ? 'ka' : 'en';
  document.documentElement.lang = lang;
};

syncDocumentLang(i18n.resolvedLanguage || i18n.language);
i18n.on('languageChanged', syncDocumentLang);

export default i18n;

